/**
 * Ground presets and the collider set.
 *
 * Colliders are what a standard can stand on: the terrain, optionally the
 * imported reference model, and the build planes the user has added.
 * Placement casts straight down from just above the click, so a leg under an
 * overhang keeps its own surface.
 */

import {
  GridHelper,
  Group,
  type Intersection,
  Mesh,
  type Object3D,
  PlaneGeometry,
  Raycaster,
  Vector3,
} from "three";
import { G } from "./geometries.ts";
import { M } from "./materials.ts";

export type TerrainKind = "flat" | "slope" | "steps";

/** How far out from a wall a standard is planted, metres. */
const WALL_OFFSET = 0.25;

/** A face this steep or flatter counts as standable. */
const UP_NORMAL_THRESHOLD = 0.6;

export type SurfacePoint = Readonly<{
  x: number;
  z: number;
  gy: number;
  onWall: boolean;
}>;

export class Terrain {
  readonly group = new Group();

  /** Horizontal centre of the ground patch, world metres. */
  private originX = 0;
  private originZ = 0;

  /** Everything a standard may stand on — terrain, reference meshes and build planes. */
  readonly colliders: Object3D[] = [];

  private readonly terrainColliders: Object3D[] = [];
  private referenceColliders: Object3D[] = [];
  private planeColliders: Object3D[] = [];
  private readonly downRay = new Raycaster();

  /**
   * Move the ground patch so it sits under `x, z`.
   *
   * The ground is a finite 90 × 60 m plane. An imported reference model keeps
   * its authored world coordinates, which are often far from the origin, so the
   * ground follows the model rather than the model moving to the ground.
   */
  setOrigin(x: number, z: number): void {
    this.originX = x;
    this.originZ = z;
    this.group.position.set(x, 0, z);
    this.group.updateMatrixWorld(true);
  }

  build(kind: TerrainKind): void {
    this.group.clear();
    this.terrainColliders.length = 0;

    const ground = kind === "slope" ? buildSlope() : new Mesh(new PlaneGeometry(90, 60), M.ground);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.group.add(ground);
    this.terrainColliders.push(ground);

    if (kind === "steps") {
      this.buildSteps();
    }

    const grid = new GridHelper(70, 70, 0xb9c1c8, 0xccd3d8);
    grid.position.y = 0.005;
    grid.material.transparent = true;
    grid.material.opacity = 0.5;
    this.group.add(grid);

    this.group.position.set(this.originX, 0, this.originZ);
    this.group.updateMatrixWorld(true);
    this.syncColliders();
  }

  private buildSteps(): void {
    const blocks: readonly (readonly [number, number, number, number, number])[] = [
      [3, 0.35, 5.5, 4, -0.2],
      [3, 0.7, 5.5, 7.2, -0.2],
      [3, 1.05, 5.5, 10.4, -0.2],
    ];

    for (const [w, h, d, x, z] of blocks) {
      const block = new Mesh(G.box, M.rock);
      block.scale.set(w, h, d);
      block.position.set(x, h / 2, z);
      block.castShadow = true;
      block.receiveShadow = true;
      this.group.add(block);
      this.terrainColliders.push(block);
    }

    const timber = new Mesh(G.box, M.wood);
    timber.scale.set(0.5, 0.22, 0.5);
    timber.position.set(0, 0.11, 1.4);
    timber.castShadow = true;
    timber.receiveShadow = true;
    this.group.add(timber);
    this.terrainColliders.push(timber);
  }

  setReferenceColliders(meshes: Object3D[]): void {
    this.referenceColliders = meshes;
    this.syncColliders();
  }

  setPlaneColliders(meshes: Object3D[]): void {
    this.planeColliders = meshes;
    this.syncColliders();
  }

  private syncColliders(): void {
    this.colliders.length = 0;
    this.colliders.push(...this.terrainColliders, ...this.referenceColliders, ...this.planeColliders);
  }

  /** Cast straight down from `fromY`; 0 when nothing is hit. */
  groundYFrom(x: number, z: number, fromY: number): number {
    this.downRay.set(new Vector3(x, fromY, z), new Vector3(0, -1, 0));
    const hit = this.downRay.intersectObjects(this.colliders, false)[0];

    return hit ? hit.point.y : 0;
  }

  groundY(x: number, z: number): number {
    return this.groundYFrom(x, z, 60);
  }

  /** Ground under a leg, starting just above its last known surface. */
  resolveGy(x: number, z: number, hintY?: number): number {
    return this.groundYFrom(x, z, hintY === undefined ? 60 : hintY + 0.3);
  }

  /**
   * Read a click: a face pointing up means the standard stands there; anything
   * steeper is a wall, and the standard goes 250 mm out from it on whatever
   * surface lies below.
   */
  surfacePoint(hit: Intersection): SurfacePoint {
    const normal = hit.face
      ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
      : new Vector3(0, 1, 0);

    if (normal.y > UP_NORMAL_THRESHOLD) {
      return { x: hit.point.x, z: hit.point.z, gy: hit.point.y, onWall: false };
    }

    const horizontal = Math.hypot(normal.x, normal.z) || 1;
    const x = hit.point.x + (normal.x / horizontal) * WALL_OFFSET;
    const z = hit.point.z + (normal.z / horizontal) * WALL_OFFSET;

    return { x, z, gy: this.groundYFrom(x, z, hit.point.y + 0.3), onWall: true };
  }
}

/** A 7.5 % ramp with a gentle cross ripple. */
function buildSlope(): Mesh {
  const geometry = new PlaneGeometry(90, 60, 64, 40);
  const position = geometry.getAttribute("position");

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    position.setZ(i, Math.max(0, x * 0.075) + Math.sin(position.getY(i) * 0.5) * 0.03);
  }
  geometry.computeVertexNormals();

  return new Mesh(geometry, M.ground);
}
